import type { ProductModel } from '@sosocio/creator-types/database';
import type {
	ProductDataModel,
	ProductSettings,
} from '@sosocio/creator-types/project';

export interface SmartLinkAttributes {
	backUrl?: string;
	cartUrl?: string;
	country?: string;
	currency?: string;
	customAttributes?: Record<string, unknown>;
	language?: string;
	printColors?: number | 'fc';
	printPositionIndexes?: number[];
	productOptions?: {
		enabled?: boolean;
		openOnStart?: boolean;
	};
	quantity?: number;
	shopid?: number;
}

export interface SmartLinkProjectContext {
	cartUrl?: ProductDataModel['cartUrl'];
	productSettings: Pick<ProductSettings, 'skuQuantity'>;
}

interface SmartLinkRequestOptions {
	baseUrl: string;
	fetch?: typeof globalThis.fetch;
	token?: string | null;
}

const REQUEST_TIMEOUT_MS = 12_000;
const WEBSHOP_REDIRECT_PATH = '/api/webshop/redirect';

/**
 * Narrows a JSON object while excluding null and array payloads.
 *
 * @param value Untrusted JSON value.
 * @returns Whether the value is a non-null, non-array object.
 */
function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Decodes numeric attributes without rounding or accepting partial numeric strings.
 *
 * @param value Untrusted quantity, identifier, or print color limit.
 * @returns A positive safe integer, or undefined for invalid numeric values.
 */
function parsePositiveInteger(value: unknown): number | undefined {
	const number = typeof value === 'string' && /^\d+$/.test(value)
		? Number(value)
		: value;

	return typeof number === 'number' && Number.isSafeInteger(number) && number > 0
		? number
		: undefined;
}

/**
 * Decodes the supported string and array forms of zero-based print positions.
 *
 * @param value Untrusted print position attribute.
 * @returns Valid position indexes, or undefined if the complete field is invalid.
 */
function parsePrintPositionIndexes(value: unknown): number[] | undefined {
	const entries: unknown = typeof value === 'string' ? value.split(',') : value;

	if (!Array.isArray(entries)) {
		return undefined;
	}

	const indexes: number[] = [];
	for (const entry of entries) {
		let index: unknown = entry;

		if (typeof entry === 'string' && /^\d+$/.test(entry.trim())) {
			index = Number(entry.trim());
		}
		if (typeof index !== 'number' || !Number.isSafeInteger(index) || index < 0) {
			return undefined;
		}

		indexes.push(index);
	}

	return indexes;
}

/**
 * Retrieves and validates one token response without retries or authentication.
 *
 * @param fetchImplementation Fetch implementation used to reach AppEngine.
 * @param url Encoded SmartLink endpoint URL.
 * @param signal Signal aborting the request when its time budget expires.
 * @returns Validated attributes, or null for HTTP, transport, JSON, or envelope failures.
 */
async function readSmartLinkResponse(
	fetchImplementation: typeof globalThis.fetch,
	url: string,
	signal: AbortSignal,
): Promise<SmartLinkAttributes | null> {
	try {
		const response = await fetchImplementation(
			url,
			{
				method: 'GET',
				signal,
			},
		);

		if (!response.ok) {
			return null;
		}

		const body: unknown = await response.json();
		return parseSmartLinkResponse(body);
	} catch {
		return null;
	}
}

/**
 * Applies an incoming cart destination while preserving the stored value when
 * no replacement is supplied. Call after restoring the targeted project and
 * resolving its incoming launch data with {@link resolveSmartLinkProjectContext}.
 * Hosts retain ownership of launch validation, hydration, and consumption.
 *
 * @param cartUrl Resolved incoming destination from {@link SmartLinkProjectContext}.
 * @param setCartUrl App-owned setter for the restored project's cart destination.
 * @returns Whether the setter completed; setter failures propagate to the caller.
 */
export function applySmartLinkProjectCartUrl(
	cartUrl: ProductDataModel['cartUrl'],
	setCartUrl: (cartUrl: NonNullable<ProductDataModel['cartUrl']>) => void,
): boolean {
	if (!cartUrl) {
		return false;
	}

	setCartUrl(cartUrl);
	return true;
}

/**
 * Applies incoming persisted-project settings while preserving the restored
 * project when the SmartLink does not supply any settings. Hosts retain
 * ownership of the state mutation so Vuex, Pinia, and other stores can merge
 * the partial {@link SmartLinkProjectContext} through their supported APIs.
 *
 * @param productSettings Resolved incoming settings from {@link SmartLinkProjectContext}.
 * @param mergeProductSettings App-owned merger for the restored project's settings.
 * @returns Whether the merger completed; merger failures propagate to the caller.
 */
export function applySmartLinkProjectSettings(
	productSettings: SmartLinkProjectContext['productSettings'],
	mergeProductSettings: (
		productSettings: SmartLinkProjectContext['productSettings'],
	) => void,
): boolean {
	if (Object.values(productSettings).every((value) => typeof value === 'undefined')) {
		return false;
	}

	mergeProductSettings(productSettings);
	return true;
}

/**
 * Builds the AppEngine webshop redirect path for one persisted project.
 * Invalid or absent IDs preserve the existing user/shop fallback contract.
 *
 * @param projectId {@link ProductModel} ID whose saved cart context should drive the redirect.
 * @returns Relative AppEngine redirect path, optionally scoped by project ID.
 */
export function buildProjectWebshopRedirectPath(
	projectId?: ProductModel['id'] | null,
): string {
	const normalizedProjectId = parsePositiveInteger(projectId);

	return normalizedProjectId === undefined
		? WEBSHOP_REDIRECT_PATH
		: `${WEBSHOP_REDIRECT_PATH}?projectId=${encodeURIComponent(normalizedProjectId)}`;
}

/**
 * Resolves a single-use launch token within a bounded time without persisting it.
 *
 * @param options AppEngine origin, optional token, and optional fetch implementation.
 * @returns Validated {@link SmartLinkAttributes}, or null when no token can be resolved.
 */
export async function fetchSmartLinkAttributes({
	baseUrl,
	fetch: fetchImplementation = globalThis.fetch,
	token,
}: SmartLinkRequestOptions): Promise<SmartLinkAttributes | null> {
	if (typeof token !== 'string' || token.trim().length === 0) {
		return null;
	}

	const controller = new AbortController();
	let timeout: ReturnType<typeof setTimeout> | undefined;
	try {
		const url = `${baseUrl.replace(
			/\/+$/,
			'',
		)}/api/smartlink/token/${encodeURIComponent(token)}`;
		return await Promise.race([
			readSmartLinkResponse(
				fetchImplementation,
				url,
				controller.signal,
			),
			new Promise<null>((resolve) => {
				timeout = setTimeout(
					() => {
						controller.abort();
						resolve(null);
					},
					REQUEST_TIMEOUT_MS,
				);
			}),
		]);
	} catch {
		return null;
	} finally {
		clearTimeout(timeout);
	}
}

/**
 * Selects supported launch attributes from the AppEngine response envelope.
 * Invalid individual fields are ignored while arbitrary custom attributes remain intact.
 *
 * @param value Untrusted JSON response containing a data object.
 * @returns Validated {@link SmartLinkAttributes}, including an empty object for valid empty data, or null for an invalid envelope.
 */
export function parseSmartLinkResponse(value: unknown): SmartLinkAttributes | null {
	if (!isRecord(value)) {
		return null;
	}
	/** AppEngine encodes an empty PHP attribute array as [] rather than {}. */
	if (Array.isArray(value.data) && value.data.length === 0) {
		return {};
	}
	if (!isRecord(value.data)) {
		return null;
	}

	const { data } = value;
	const attributes: SmartLinkAttributes = {};
	const stringKeys = [
		'backUrl',
		'cartUrl',
		'country',
		'currency',
		'language',
	] as const;
	for (const key of stringKeys) {
		if (typeof data[key] === 'string' && data[key].trim().length > 0) {
			attributes[key] = data[key];
		}
	}

	if (isRecord(data.customAttributes)) {
		attributes.customAttributes = data.customAttributes;
	}

	const quantity = parsePositiveInteger(data.quantity);
	const shopid = parsePositiveInteger(data.shopid);
	const printColors = data.printColors === 'fc' ? 'fc' : parsePositiveInteger(data.printColors);

	if (quantity !== undefined) {
		attributes.quantity = quantity;
	}
	if (shopid !== undefined) {
		attributes.shopid = shopid;
	}
	if (printColors !== undefined) {
		attributes.printColors = printColors;
	}

	const indexes = parsePrintPositionIndexes(data.printPositionIndexes);

	if (indexes) {
		attributes.printPositionIndexes = indexes;
	}
	if (isRecord(data.productOptions)) {
		const productOptions: NonNullable<SmartLinkAttributes['productOptions']> = {};

		if (typeof data.productOptions.enabled === 'boolean') {
			productOptions.enabled = data.productOptions.enabled;
		}
		if (typeof data.productOptions.openOnStart === 'boolean') {
			productOptions.openOnStart = data.productOptions.openOnStart;
		}
		if (Object.keys(productOptions).length > 0) {
			attributes.productOptions = productOptions;
		}
	}

	return attributes;
}

/**
 * Builds the canonical persisted-project values supplied by a SmartLink.
 * Host applications retain ownership of when this context is applied or consumed.
 *
 * @param attributes Validated {@link SmartLinkAttributes}, or no active SmartLink context.
 * @returns {@link SmartLinkProjectContext} shared by Creator and kiosk.
 */
export function resolveSmartLinkProjectContext(
	attributes?: Readonly<Pick<SmartLinkAttributes, 'cartUrl' | 'quantity'>> | null,
): SmartLinkProjectContext {
	return {
		...(attributes?.cartUrl ? {
			cartUrl: attributes.cartUrl,
		} : {}),
		productSettings: (
			attributes?.quantity !== undefined
				? {
					skuQuantity: attributes.quantity,
				}
				: {}
		),
	};
}
